import { FC, ReactNode, useMemo, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { CheckPicker, Loader } from 'rsuite'
import { useClearRefinements, useRefinementList } from 'react-instantsearch'
import sortBy from 'lodash/sortBy'
import { ItemDataType } from 'rsuite/esm/@types/common'

import useFiltersClose from '../../hooks/useFiltersClose'
import { useApi } from '../../../services/useApi'
import useUrls from '../../../services/useUrls'
import useIsOnMobile from '../../../services/useIsOnMobile'
import CheckCircleIcon from '../../Icons/CheckCircleIcon'
import { createUrlParams } from '../../../lib/params'
import { getPickerValue, handlePickerChange } from './util'
import { getCountryAndLocaleStrings } from '../../../utils/locales'
import { ProductAttribute } from '../../../utils/types/Product'
import { FilterItem, isDefined } from '../../../utils/types/misc'

import styles from '../../../styles/CatalogFilters.module.less'

const COLORS = ['red', 'brown', 'orange', 'yellow', 'green', 'purple', 'blue', 'white', 'grey', 'black', 'multicolor']

interface ColorPickerProps {
  label: string
  shouldCloseFilters: boolean
  filterActions?: {
    updateAppliedFilter?: (filterKey: string, filterItem: FilterItem) => void
    removeAppliedFilter?: (filterKey: string) => void
    removeAppliedFilterItem?: (filterKey: string, value: string | number) => void
  }
}

const ColorPicker: FC<ColorPickerProps> = (props) => {
  const {
    label,
    shouldCloseFilters,
    filterActions: {
      updateAppliedFilter,
      removeAppliedFilter,
      removeAppliedFilterItem,
    } = {},
  } = props

  const { getResource } = useApi()
  const { pushT } = useUrls()
  const { query, asPath, locale } = useRouter()
  const { locale: lang } = getCountryAndLocaleStrings(locale)
  const initializedRef = useRef(false)
  const isOpen = useFiltersClose(shouldCloseFilters)
  const isOnMobile = useIsOnMobile()

  const filterCode = 'f_filter-color'
  const pickerValue = useMemo(() => getPickerValue(query, filterCode), [query])

  const filtersRef = useRef<HTMLDivElement>(null)
  const containerRef = filtersRef.current || undefined

  const {
    refine: clear,
  } = useClearRefinements({
    includedAttributes: [`filter-color.${lang}`],
  })
  const {
    items,
    refine,
  } = useRefinementList({
    attribute: `filter-color.${lang}`,
    operator: 'or',
    limit: 100,
  })

  const {
    data: attributes,
    isLoading,
  } = getResource<ProductAttribute>('attribute', createUrlParams({
    filter: {
      '==': {
        field: 'attribute.type',
        value: 'filter-color',
      },
    },
    page: { limit: '25' },
  }))

  useEffect(() => {
    // Refine instantsearch filter items when loading page
    const refined = items.filter((item) => item.isRefined)
    if (!initializedRef.current && !isLoading && refined.length === 0 && pickerValue.length !== 0) {
      // Only trigger "on load" and not on later url changes
      initializedRef.current = true
      attributes
        .filter((attribute) => pickerValue.includes(attribute.id))
        .filter(isDefined)
        .forEach((attribute) => {
          refine(attribute['attribute.label'])
          if (updateAppliedFilter) {
            updateAppliedFilter(filterCode, {
              label: attribute['attribute.label'] || '',
              value: attribute.id,
            })
          }
        })
    }
  }, [items, pickerValue, isLoading, refine, updateAppliedFilter])

  const data = attributes
    .filter((attr) => (
      items.find((item) => item.label === attr['attribute.label'])
    ))
    .map((attr) => ({
      label: attr['attribute.label'],
      value: attr.id,
      color: attr['attribute.code'],
    }))

  const renderMenu = (menu: ReactNode) => {
    if (isLoading) {
      return (
        <Loader
          className={`${styles.loader}`}
          content="Loading..."
        />
      )
    }
    return (
      <div className={styles['color-menu']}>
        {menu}
      </div>
    )
  }

  const handleChange = (newValue: string[]) => {
    initializedRef.current = true
    const isClear = newValue.length < pickerValue.length
    const isNew = (id: string) => !pickerValue.includes(id)
    if (isClear) {
      clear()

      // Clear from filter modal state
      if (newValue.length === 0 && removeAppliedFilter) {
        removeAppliedFilter(filterCode)
      } else if (removeAppliedFilterItem) {
        // Can use find since the picker only supports removing a single value at a time
        const removedValue = pickerValue.find((id) => !newValue.includes(id)) || ''
        removeAppliedFilterItem(filterCode, removedValue)
      }
    }
    attributes
      .filter((attribute) => (
        newValue.includes(attribute.id) && (isClear || isNew(attribute.id))))
      .filter(isDefined)
      .forEach((attribute) => {
        refine(attribute['attribute.label'])
        if (updateAppliedFilter) {
          updateAppliedFilter(filterCode, {
            label: attribute['attribute.label'] || '',
            value: attribute.id,
          })
        }
      })

    handlePickerChange(pushT, query, asPath, filterCode)(newValue)
  }

  const renderMenuItem = (itemLabel: ReactNode, item: ItemDataType) => (
    <div className={styles['color-item']}>
      <div className={styles['grid-wrapper']}>
        <CheckCircleIcon className={styles['selected-color-icon']} />
        <div className={styles['color-grid']}>
          <div
            className={`${styles['color-square']} ${styles[item.color]} ${styles['shade-1']}`}
            style={!COLORS.includes(item.color) ? { backgroundColor: item.color } : {}}
          />
          <div
            className={`${styles['color-square']} ${styles[item.color]} ${styles['shade-2']}`}
            style={!COLORS.includes(item.color) ? { backgroundColor: item.color } : {}}
          />
          <div
            className={`${styles['color-square']} ${styles[item.color]} ${styles['shade-3']}`}
            style={!COLORS.includes(item.color) ? { backgroundColor: item.color } : {}}
          />
          <div
            className={`${styles['color-square']} ${styles[item.color]} ${styles['shade-4']}`}
            style={!COLORS.includes(item.color) ? { backgroundColor: item.color } : {}}
          />
        </div>
      </div>
      <p className={styles.text}>{itemLabel}</p>
    </div>
  )

  const colorOrder = (item: { label: string, color: string }): number => {
    // Hard coded ordering of colors
    const order = COLORS
    if (order.includes(item.color)) {
      return order.indexOf(item.color)
    }
    return order.length
  }
  const sortedData = sortBy(data, [(item) => colorOrder(item)])

  if (isOnMobile) {
    return (
      <div className={`rs-picker-menu ${styles['mobile-picker']}`}>
        <div className="rs-pick-checker-menu">
          {renderMenu(
            <div className="rs-picker-check-menu rs-picker-check-menu-items">
              {sortedData.map((item) => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <div
                  key={item.value}
                  role="option"
                  aria-selected={pickerValue.includes(item.value || '')}
                  aria-disabled="false"
                  tabIndex={-1}
                  onClick={() => {
                    if (pickerValue.includes(item.value || '')) {
                      handleChange(pickerValue.filter((oldValue) => oldValue !== item.value))
                    } else {
                      handleChange([...pickerValue, item.value || ''])
                    }
                  }}
                >
                  <div className={`rs-check-item rs-checkbox ${pickerValue.includes(item.value || '') ? 'rs-checkbox-checked' : ''}`}>
                    <div className="rs-checkbox-checker">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label>
                        {renderMenuItem(item.label, item)}
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>,
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={filtersRef}
      className="relative"
      data-testid={`${filterCode}-picker-container`}
    >
      <CheckPicker
        data={sortedData}
        value={pickerValue}
        placeholder={label}
        label={pickerValue.length ? label : undefined}
        cleanable={false}
        open={isOpen}
        container={containerRef}
        block
        placement="bottomEnd"
        searchable={false}
        renderMenu={renderMenu}
        renderMenuItem={renderMenuItem}
        onChange={handleChange}
      />
    </div>
  )
}

export default ColorPicker
